import { MoonOutlined, SunOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Flex,
  Segmented,
  Space,
  Tooltip,
  Typography,
  theme,
} from "antd";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../common/constants";
import { usePreferences } from "../hooks/usePreferences";
import julebLogo from "../assets/images/julebLogo.svg";

interface AuthCardProps {
  title: string;
  subtitle?: string;
  children: ReactNode;
}

/**
 * Shared shell for every auth page (login, logout, forgot/reset password):
 * gradient background, centred card, logo + heading, and the theme and language
 * controls.
 *
 * The background gradients are the one sanctioned place in this app for
 * hardcoded colors; everything else comes from antd tokens.
 */
export function AuthCard({ title, subtitle, children }: AuthCardProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { currentLanguage, changeLanguage, isDarkMode, toggleTheme } =
    usePreferences();

  const background = isDarkMode
    ? "linear-gradient(135deg, rgba(254,106,0,0.10) 0%, rgba(20,20,20,1) 45%, rgba(10,10,10,1) 100%)"
    : "linear-gradient(135deg, rgba(254,106,0,0.18) 0%, rgba(255,255,255,1) 45%, rgba(34,36,40,0.08) 100%)";

  return (
    <Flex
      align="center"
      justify="center"
      style={{
        minHeight: "100vh",
        // Keeps the card off the viewport edge on narrow screens.
        padding: token.paddingLG,
        background,
        transition: "background 0.3s ease",
      }}
    >
      <Card
        variant="borderless"
        style={{
          width: "100%",
          maxWidth: 440,
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
        }}
        styles={{ body: { padding: 32 } }}
      >
        <Flex vertical gap={24}>
          <Flex align="flex-start" justify="space-between" gap={16} wrap>
            <Space direction="vertical" size={8}>
              <img src={julebLogo} alt="Juleb" style={{ height: 28 }} />
              <Typography.Title level={2} style={{ margin: 0 }}>
                {title}
              </Typography.Title>
              {subtitle && (
                <Typography.Text type="secondary">{subtitle}</Typography.Text>
              )}
            </Space>

            <Space size={8}>
              <Tooltip
                title={
                  isDarkMode
                    ? t("auth.login.lightMode")
                    : t("auth.login.darkMode")
                }
              >
                <Button
                  type="text"
                  shape="circle"
                  aria-label={
                    isDarkMode
                      ? t("auth.login.lightMode")
                      : t("auth.login.darkMode")
                  }
                  icon={isDarkMode ? <SunOutlined /> : <MoonOutlined />}
                  onClick={toggleTheme}
                />
              </Tooltip>
              <Segmented
                value={currentLanguage}
                onChange={(value) =>
                  changeLanguage(value as SUPPORTED_LANGUAGES)
                }
                options={[
                  { label: t("language.en"), value: SUPPORTED_LANGUAGES.EN },
                  { label: t("language.ar"), value: SUPPORTED_LANGUAGES.AR },
                ]}
              />
            </Space>
          </Flex>

          {children}
        </Flex>
      </Card>
    </Flex>
  );
}

export default AuthCard;
